import { useEffect, useState } from "react";
import StudentActivityPreview from "@/components/student/StudentActivityPreview";
import type { QuestionRecord } from "@/components/courses/QuestionEditor";
import { api, errorMessage } from "@/lib/api";
import {
  activityHasQuestions,
  activityIncludes,
  activityRequireKnowledgeOn,
  assignmentPointsOf,
  partsFromBody,
} from "@/lib/activityAnatomy";
import { inferSlideMode, normalizeSlideDecks } from "@/lib/slideDecks";
import "@/styles/student.css";
import "./StudentPreviewDialog.css";

/** The fields of an activity this view needs (a course structure row has them all). */
export type StudentItemViewItem = {
  id: string;
  type: string;
  label?: string;
  title?: string | null;
  body?: unknown;
  content?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
  estimatedMinutes?: number | null;
  timeLimitMinutes?: number | null;
  maxAttempts?: number | null;
  dueAt?: string | null;
};

/**
 * One item exactly as a student sees it — the same renderer as the course
 * preview. Read-only: questions are loaded to show them, nothing is submitted.
 */
export default function StudentItemView({ item }: { item: StudentItemViewItem }) {
  const metadata = (item.metadata || {}) as Record<string, unknown>;
  const hasQuestions = activityHasQuestions(item.type, metadata as { hasQuestions?: unknown });
  const [questions, setQuestions] = useState<QuestionRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!hasQuestions) {
      setQuestions([]);
      setLoading(false);
      setError("");
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    setQuestions([]);
    api<{ success: boolean; data?: QuestionRecord[] }>(`/api/activities/${item.id}/questions`)
      .then((envelope) => {
        if (!cancelled) setQuestions(Array.isArray(envelope.data) ? envelope.data : []);
      })
      .catch((err) => {
        if (!cancelled) setError(errorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [item.id, hasQuestions]);

  const content = (item.content || {}) as Record<string, unknown>;
  const decks = normalizeSlideDecks(content.decks);
  const terms = (Array.isArray(content.terms) ? content.terms : [])
    .filter((row): row is { id?: unknown; term?: unknown; definition?: unknown } => Boolean(row) && typeof row === "object")
    .map((row, i) => ({
      id: String(row.id || i),
      term: String(row.term || ""),
      definition: String(row.definition || ""),
    }));

  return (
    <div className="acuity-student acuity-student--preview">
      {error ? <p className="al-course-preview-warn">{error}</p> : null}
      <StudentActivityPreview
        type={item.type}
        title={item.title?.trim() || item.label || item.type}
        estimatedMinutes={Number(item.estimatedMinutes) || 0}
        dueAt={item.dueAt || ""}
        timeLimitMinutes={Number(item.timeLimitMinutes) || 0}
        maxAttempts={item.maxAttempts == null ? "" : Number(item.maxAttempts)}
        includes={activityIncludes(item.type, metadata as { includes?: unknown }, {
          dueAt: item.dueAt || null,
          timeLimitMinutes: item.timeLimitMinutes ?? null,
          maxAttempts: item.maxAttempts ?? null,
        })}
        parts={partsFromBody(item.body)}
        questions={questions}
        questionsLoading={loading}
        hasQuestions={hasQuestions}
        requireKnowledge={activityRequireKnowledgeOn(metadata as { requireKnowledge?: unknown })}
        terms={terms}
        decks={decks}
        slideMode={inferSlideMode(content.slideMode, decks)}
        assignmentPoints={assignmentPointsOf(item.type, metadata as { assignmentPoints?: unknown; hasQuestions?: unknown })}
      />
    </div>
  );
}
